import { assign } from '@atds-legacy/lang';
import dayjs from 'dayjs';
import { Component, SDK, deserialize } from '@redge-media/fo-js-sdk';
import { storage } from '@/shared/plugins/storage';
import { setProfileCookie } from '@/shared/utils/subscriber-helper';
import { metaSetup } from '@/shared/utils/seo/meta-setup';
import UserRole from '@/shared/enums/user-role';
import subscriberModel from './subscriber-model';

const DEFAULT_AUTH_TOKEN_INTERVAL = 60 * 1000;
const DEFAULT_AUTH_TOKEN_THRESHOLD = 5 * 60 * 1000;
const RETRY_TIMEOUTS_ON_ERROR = [1000, 60000, 180000, 300000, 600000, 900000];
let errorTimeoutIndex = 0;

const Subscriber = Component.mixin({ deserialize }).extend({
  method: 'get',

  constructor: function Subscriber(info, time) {
    Component.call(assign(this, {
      info,
      time,
    }));

    this.attrs = [];
    this.endpoint = 'subscribers';
    this.isFetched = false;
    this._profile = undefined;
    this.currentLanguage = undefined;

    assign(this, subscriberModel);

    this.info.on('sync', () => {
      const lang = storage.language || this.info.defaultLanguage;

      this.checkTesterLanguage();
      this.changeLanguage(lang.toUpperCase());
    });

    this.on('sync:profile', (profile) => {
      this.profile = profile;
    });

    this.on('login', () => {
      this.checkTesterLanguage();
    });

    this.on('login:auto', () => {
      this.checkTesterLanguage();
    });

    this.on('logout', () => {
      this.checkTesterLanguage();
    });
  },

  get isKidsTheme() {
    // Fallback to undefined to prevent sending unnecessary kids param
    return (this.profile && this.profile.kids) || storage.isKidsMode || undefined;
  },

  get isLogged() {
    return this.isFetched && !!this.id;
  },

  get isSso() {
    return this.ssoUser === false;
  },

  get profile() {
    if (!this._profile && this.isLogged) {
      this._profile = this.profiles.find((p) => p.id === this.activeProfileId);
    }

    return this._profile;
  },

  set profile(profile) {
    if (this.isLogged) {
      this._profile = profile;
    }
  },

  get isAmb() {
    return this.currentTenant.includes('AMB');
  },

  get isTester() {
    return this.hasRole(UserRole.TESTER);
  },

  get isNoAds() {
    return this.hasRole(UserRole.NO_ADS);
  },

  get currentTenant() {
    if (this.isLogged) {
      return this.tenant;
    }

    return this.info.defaultTenant;
  },

  get hasConsents() {
    return this.status && this.status.missingAgreements === false && this.agreementsShown === true;
  },

  get isFirstLogin() {
    // TODO to change along with password change
    return false;
  },

  hasRole(role) {
    if (!this.isFetched) {
      return false;
    }

    return this.roles.some((_role) => _role.name === role);
  },

  switchProfile(profile = {}) {
    const refresh = (this.activeProfileId !== profile.id);

    this.activeProfileId = profile.id;
    const previousProfile = this.profile;
    setProfileCookie(profile);

    if (refresh) {
      this.emit('change:profile', {
        previousProfile,
        profile,
      });
      this.emit('sync:profile', profile);
    }
  },

  fetch(opts = {}) {
    const prevLogged = this.isLogged;
    const prevTenant = this.currentTenant;

    return SDK.apiAdapter.get(`${this.endpoint}/detail`).then((response) => {
      const data = this.deserialize(response, opts);

      this.attrs = Object.keys(data);
      assign(this, data);

      this.isFetched = true;
      this.bind();
      this.emit('sync', this.method, this);

      if (prevLogged !== this.isLogged) {
        this.emit('login:auto', this);
      }

      this.syncTenant(prevTenant);

      return this;
    }).catch((e) => {
      if (opts.forceError) {
        throw e;
      }

      this.isFetched = false;
      this.unbind();
      this.reset();
      this.emit('sync', this.method, this);
      this.syncTenant(prevTenant);

      if (e.response.status !== 403) {
        throw e;
      } else {
        const code = e.response.code || e.response.data.code;
        this.emit('error', code, this);
      }

      return this;
    });
  },

  login(data, opts = {}) {
    const prevTenant = this.currentTenant;

    return SDK.apiAdapter.post(`${this.endpoint}/login`, data).then((response) => {
      const data = this.deserialize(response, opts);

      this.attrs = Object.keys(data);
      assign(this, data);

      this.isFetched = true;
      this.bind();
      this.emit('sync', this.method, this);
      this.emit('login', this);
      this.syncTenant(prevTenant);

      return this;
    });
  },

  register(data, isCaptchaV3) {
    return SDK.apiAdapter.post(`${this.endpoint}/register`, data, { params: { isCaptchaV3 } }).then(() => {
      this.emit('register');
      return this;
    });
  },

  logout() {
    const prevTenant = this.currentTenant;
    const params = { tenant: prevTenant, profile: this.profile, id: this.id };

    const logout = () => {
      this.isFetched = false;
      this.unbind();
      this.reset();
      this.emit('sync', this.method, this);
      this.emit('logout:stats', params, this);
      this.emit('logout', params, this);
      this.syncTenant(prevTenant);
    };

    return SDK.apiAdapter.post(`${this.endpoint}/logout`)
      .then(() => logout())
      .catch((e) => {
        if (e.response && e.response.status === 403) {
          logout();
        }
      });
  },

  refreshHttpSession(opts = {}) {
    clearTimeout(this.sessionTimeout);

    if (!this.isLogged) {
      return Promise.resolve();
    }

    if (!opts.force && this.httpSessionTill && !this.time.now.isAfter(this.httpSessionTill)) {
      return;
    }

    if (opts.now && opts.till) {
      const next = (opts.till - opts.now) / 2;
      this.httpSessionTill = opts.now + next;
      this.sessionTimeout = setTimeout(() => this.refreshHttpSession(), next);
      return Promise.resolve();
    }

    return SDK.apiAdapter.put(`${this.endpoint}/httpsession`)
      .then((response) => {
        errorTimeoutIndex = 0;
        const data = this.deserialize(response, opts);
        const next = (data.till - data.now) / 2;
        this.httpSessionTill = data.now + next;
        this.sessionTimeout = setTimeout(() => this.refreshHttpSession(), next);
      }).catch((e) => {
        const response = e && e.response;
        if (!response || (response.status % 100 === 5)) {
          const next = RETRY_TIMEOUTS_ON_ERROR[Math.min(errorTimeoutIndex, RETRY_TIMEOUTS_ON_ERROR.length - 1)];
          this.httpSessionTill += next;
          this.sessionTimeout = setTimeout(() => this.refreshHttpSession(), next);
          errorTimeoutIndex++;
        } else {
          // Attempt to log in user after the session has expired
          return this.fetch({ forceError: true }).catch(() => this.logout());
        }
      });
  },

  requestPasswordReset({ login, captcha }, isCaptchaV3) {
    return SDK.apiAdapter.put(`${this.endpoint}/password/reset`, {
      login,
      captcha,
    }, { params: { isCaptchaV3 } });
  },

  confirmPasswordReset({ login, newPassword, confirmationCode, medium }) {
    return SDK.apiAdapter.post(`${this.endpoint}/password/reset`, {
      login,
      newPassword,
      confirmationCode,
      medium,
    });
  },

  resetPassword({ passwordResetToken, newPassword, appVersion }) {
    return SDK.apiAdapter.put(`${this.endpoint}/password/reset/token`, {
      passwordResetToken,
      newPassword,
      appVersion,
    });
  },

  changeEmail({ id, password, email }) {
    return SDK.apiAdapter.put(`${this.endpoint}/email`, {
      id,
      password,
      email,
    });
  },

  confirmChangeEmail({ id, password, newEmail, confirmationCode }) {
    return SDK.apiAdapter.post(`${this.endpoint}/email`, {
      id,
      password,
      newEmail,
      confirmationCode,
    });
  },

  checkTesterLanguage() {
    const canAccessEnglish = this.isTester || process.env.NODE_ENV !== 'production';

    if (canAccessEnglish && !this.info.languages.includes('EN')) {
      this.info.languages.push('EN');
    }

    if (!canAccessEnglish && this.info.languages.includes('EN')) {
      this.info.languages = this.info.languages.filter((lang) => lang !== 'EN');

      if (storage.language === 'EN') {
        this.changeLanguage(this.info.defaultLanguage);
      }
    }
  },

  resendConfirmationLink({ email }) {
    return SDK.apiAdapter.post(`${this.endpoint}/mail/verify/resend?email=${encodeURIComponent(email)}`);
  },

  changePhone({ msisdn, password }) {
    return SDK.apiAdapter.put(`${this.endpoint}/msisdn`, {
      msisdn,
      password,
    });
  },

  confirmPhoneChange(payload = {}) {
    return SDK.apiAdapter.post(`${this.endpoint}/msisdn`, payload);
  },

  verifyPin(pin) {
    return SDK.apiAdapter.post(`${this.endpoint}/adult/pin`, { pin });
  },

  updatePin({ pin, password }) {
    return SDK.apiAdapter.put(`${this.endpoint}/adult/pin`, {
      pin,
      password,
    });
  },

  updateGender({ gender }) {
    return SDK.apiAdapter.put(`${this.endpoint}/gender`, {
      gender,
    });
  },

  updateDateOfBirth({ dateOfBirth }) {
    return SDK.apiAdapter.put(`${this.endpoint}/birth`, {
      dateOfBirth,
    });
  },

  reset() {
    this.attrs.forEach((key) => {
      delete this[key];
    });

    this._profile = undefined;

    this.attrs = [];
    assign(this, subscriberModel);
  },

  bind() {
    this.unbind();
    this.refreshHttpSession(this.httpSession);
  },

  unbind() {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      delete this.sessionTimeout;
    }
  },

  getAgreement(id) {
    return SDK.apiAdapter.get(`${this.endpoint}/agreements/${id}`)
      .then((res) => this.deserialize(res));
  },

  getAgreements(group) {
    return SDK.apiAdapter.get(`${this.endpoint}/agreements`, { group })
      .then((res) => this.deserialize(res));
  },

  saveAgreements(agreements = []) {
    return SDK.apiAdapter.post(`${this.endpoint}/agreements`, { agreements })
      .then((res) => this.deserialize(res));
  },

  changePassword({ id, oldPassword, newPassword, captcha }, isCaptchaV3) {
    return SDK.apiAdapter.put(`${this.endpoint}/password`, {
      id,
      oldPassword,
      newPassword,
      captcha,
    }, { params: { isCaptchaV3 } });
  },

  setPassword(password) {
    return SDK.apiAdapter.post(`${this.endpoint}/password/`, { password });
  },

  changeLanguage(lang) {
    if (lang === this.currentLanguage) {
      return;
    }

    this.currentLanguage = lang;
    storage.language = lang;
    metaSetup({ lang });
    this.emit('sync:language', lang);
  },

  verifyOneTimeCode({ code }) {
    return SDK.apiAdapter.put(`${this.endpoint}/devices/otc`, { code });
  },

  setKidsMode(value) {
    storage.isKidsMode = value;

    if (this.active) {
      this.switchProfile(this.profiles.find((profile) => {
        return value ? profile.kids : !profile.kids;
      }));
    }
    this.emit('sync:kids');
  },

  syncTenant(prevTenant) {
    if (prevTenant === this.currentTenant) {
      return;
    }

    this.emit('sync:tenant', this.currentTenant);
  },

  updateAuthToken() {
    return SDK.apiAdapter.get(`${this.endpoint}/auth/token`, { id: this.id })
      .then(this.deserialize)
      .then((token) => {
        this.authToken = token;
        this.checkAuthToken();
      })
      .catch(() => {});
  },

  checkAuthToken() {
    if (this.authTokenInterval) {
      clearInterval(this.authTokenInterval);
    }

    if (!this.authToken || !this.authToken.till) {
      return;
    }

    const tokenTill = dayjs(this.authToken.till);
    const interval = this.info.platformSettings.authTokenInterval || DEFAULT_AUTH_TOKEN_INTERVAL;
    const threshold = this.info.platformSettings.authTokenThreshold || DEFAULT_AUTH_TOKEN_THRESHOLD;

    this.authTokenInterval = setInterval(() => {
      const safetyThreshold = this.time.now.add(threshold, 'milliseconds');

      if (this.time.now.isAfter(tokenTill)) {
        this.clearInterval(this.authTokenInterval);
        this.authToken = undefined;
        this.authTokenInterval = undefined;
        return;
      }

      if (safetyThreshold.isAfter(tokenTill)) {
        this.updateAuthToken();
      }
    }, interval);
  },
});

export default Subscriber;
